import {
	GetInvestmentVehicleRelatedPeopleRes,
	PersonRelationType,
} from '@dealstack/shared/lib/generated';
import { CONTROLLING_PARTY_RELATION_TYPES } from '@dealstack/shared/lib/common/business/controllingPartyRelationTypes.ts';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	RelatedPersonCardRow,
	RelatedPersonRole,
	RelatedPersonRow,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestmentVehicleDetails/components/RelatedPeopleCard/relatedPeople.types.ts';

const RELATION_TYPE_DISPLAY_ORDER = [
	PersonRelationType.PIV,
	PersonRelationType.REPRESENTATIVE,
	...CONTROLLING_PARTY_RELATION_TYPES,
] as const;

const ROLE_LABELS: Record<PersonRelationType, string> = {
	[PersonRelationType.PIV]: 'investors.details.related_people.roles.investor',
	[PersonRelationType.REPRESENTATIVE]: 'investors.details.related_people.roles.representative',
	[PersonRelationType.DIRECTOR]: 'investors.details.related_people.roles.director',
	[PersonRelationType.UBO]: 'investors.details.related_people.roles.ubo',
	[PersonRelationType.PSC]: 'investors.details.related_people.roles.psc',
	[PersonRelationType.TRUSTEE]: 'investors.details.related_people.roles.trustee',
	[PersonRelationType.SETTLER_OR_GRANTOR]:
		'investors.details.related_people.roles.settler_or_grantor',
	[PersonRelationType.PROTECTOR_OR_ENFORCER]:
		'investors.details.related_people.roles.protector_or_enforcer',
	[PersonRelationType.BENEFICIARY]: 'investors.details.related_people.roles.beneficiary',
	[PersonRelationType.OTHER]: 'investors.details.related_people.roles.other',
};

const toRelatedPersonRows = (
	data: GetInvestmentVehicleRelatedPeopleRes | undefined
): RelatedPersonRow[] =>
	data
		? RELATION_TYPE_DISPLAY_ORDER.flatMap((relationType) =>
				data[relationType].map((person) => ({
					...person,
					relationType,
					investorId: 'investorId' in person ? person.investorId : null,
				}))
			)
		: [];

export const toRelatedPersonCards = (
	data: GetInvestmentVehicleRelatedPeopleRes | undefined
): RelatedPersonCardRow[] => {
	const byPersonId = new Map<string, RelatedPersonCardRow>();

	for (const row of toRelatedPersonRows(data)) {
		const role: RelatedPersonRole = {
			relationId: row.relationId,
			relationType: row.relationType,
			customRelationType: row.customRelationType,
		};

		const card = byPersonId.get(row.id);

		if (card) {
			card.roles.push(role);
			continue;
		}

		byPersonId.set(row.id, {
			id: row.id,
			displayName: row.displayName,
			email: row.email,
			taxResidence: row.taxResidence,
			roles: [role],
			investorId: row.investorId,
		});
	}

	return [...byPersonId.values()];
};

export const getRoleLabel = (
	role: RelatedPersonRole,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>
): string => {
	if (role.relationType === PersonRelationType.OTHER && role.customRelationType) {
		return role.customRelationType;
	}

	return tMipManage(ROLE_LABELS[role.relationType]);
};
